import { skyStateFromForecast } from './sky/model'
import { CONDITIONS } from '../shared/conditions'
import type { HourPoint, WeatherReport } from '../shared/contract'
import { fmtDistance, fmtHour, fmtPercent, fmtPressure, fmtTemp, fmtTime, fmtWeekday, fmtWind, formatter, localDate, type Units } from '../shared/units'
import type { TimeModels } from '../app/time/hourModels'
import type { V5Frame } from './data/types'
import type { SkyState } from './sky/types'

export const unavailable = '—'
const number = (v: number | null | undefined) => v == null || !Number.isFinite(v) ? unavailable : String(Math.round(v))

export function skyState(report: WeatherReport, sample: WeatherReport['current'] | HourPoint, time: number, demo: boolean): SkyState {
  return skyStateFromForecast({ ...sample, demo }, report.location, time)
}

/** Factual story templates. Units and times have one formatter; no invented accuracy or minute precision. */
export function weatherStory(report: WeatherReport, units: Units): { headline: string; accent: string; summary: string } {
  const c = report.current.condition
  const now = Date.parse(report.current.time)
  // Minute onset/stop timing has one authority; the story and nowcast quote the supplied headline.
  const minute = report.minutely
  const nearTerm = minute.status !== 'dry' && minute.status !== 'unavailable' && minute.interval_minutes > 0 && minute.points.some((p) =>
    Number.isFinite(p.intensity_mmh) && p.intensity_mmh >= 0 && Date.parse(p.time) >= now && Date.parse(p.time) <= now + 7_200_000,
  ) ? minute.headline : null
  const wet = report.hourly.find((hour) => (hour.precip_probability ?? 0) >= 50 && Date.parse(hour.time) > now && Date.parse(hour.time) <= now + 36 * 3_600_000)
  const wetKind = wet?.precip_type === 'snow' ? 'Snow' : wet?.precip_type === 'sleet' || wet?.precip_type === 'mix' || wet?.precip_type === 'freezing_rain' ? 'Wintry weather' : 'Rain'
  const wetDay = wet ? localDate(wet.time, report.location.timezone) : ''
  const today = localDate(report.current.time, report.location.timezone)
  const high = report.daily[0]?.high_c
  return {
    headline: `${CONDITIONS[c]?.label ?? report.current.description} now.`,
    accent: nearTerm || (wet ? `${wetKind} ${wetDay === today ? 'around' : `${fmtWeekday(wetDay, report.location.timezone, new Date(now))} at`} ${fmtHour(wet.time, report.location.timezone, units.clock)}.` :
      high == null ? 'Daily high unavailable.' : `High ${fmtTemp(high, units.temp)} today.`),
    summary: report.summaries.today || report.summaries.hourly || report.current.description,
  }
}

export function buildFrames(report: WeatherReport, models: TimeModels, units: Units, demo: boolean): { now: V5Frame; hours: V5Frame[] } {
  const story = weatherStory(report, units)
  const c = report.current
  const stamp = (iso: string) => `${formatter(report.location.timezone, { month: 'short', day: 'numeric' }).format(new Date(iso))}, ${fmtTime(iso, report.location.timezone, units.clock)}`
  const currentStamp = stamp(c.time)
  const issueStamp = stamp(report.generated_at)
  const now: V5Frame = {
    time: Date.parse(c.time), index: 0, stamp: 'Now', temperature: fmtTemp(c.temperature_c, units.temp), condition: c.condition,
    description: c.description, night: !c.is_day, feels: fmtTemp(c.apparent_c, units.temp), wind: fmtWind(c.wind_ms, units.wind),
    humidity: fmtPercent(c.humidity_pct), pressure: fmtPressure(c.pressure_hpa, units.pressure), visibility: fmtDistance(c.visibility_m, units.distance),
    uv: number(c.uv_index), dew: fmtTemp(c.dewpoint_c, units.temp), aqi: number(report.air_quality?.aqi),
    ...story,
    provenance: demo ? 'Demo scenario · not a current forecast' : `${c.provenance.method === 'observation' ? 'Observed' : c.provenance.method === 'model' ? 'Modeled' : 'Fused'} · ${currentStamp}`,
    sky: skyState(report, c, Date.parse(c.time), demo),
  }
  return { now, hours: models.hours.map((hour) => ({
    time: hour.t, index: hour.index, stamp: hour.stamp, temperature: hour.temp ?? unavailable, condition: hour.condition,
    description: hour.conditionText, night: !hour.isDay, feels: hour.feels ?? unavailable, wind: hour.wind ?? unavailable,
    humidity: hour.humidity ?? unavailable, pressure: hour.pressure ?? unavailable, visibility: hour.visibility ?? unavailable,
    uv: hour.uv ?? unavailable, dew: hour.dewPoint ?? unavailable, aqi: hour.aqi ?? unavailable,
    headline: `${hour.conditionText}.`, accent: `${hour.temp ?? unavailable} · ${hour.stamp}`,
    summary: `Hourly forecast for ${hour.stamp}. Return to Now for the current observation and today’s briefing.`,
    provenance: demo ? 'Demo hourly forecast · not a current forecast' : `Hourly forecast · issued ${issueStamp}`,
    sky: skyState(report, hour.hour, hour.t, demo),
  })) }
}

const frameCache = new WeakMap<TimeModels, ReturnType<typeof buildFrames>>()
/** Prepared off the input path for adjacent places; selected binding reuses the exact same frames. */
export function getFrames(report: WeatherReport, models: TimeModels, units: Units, demo: boolean): ReturnType<typeof buildFrames> {
  const cached = frameCache.get(models)
  if (cached) return cached
  const frames = buildFrames(report, models, units, demo)
  frameCache.set(models, frames)
  return frames
}
